import React, { useState, useEffect } from 'react';
import { PromptCard, PromptGroup as PromptGroupType } from '../types';
import PromptGroup from './PromptGroup';
import {
  fetchPromptCards,
  fetchPromptGroups,
  createPromptCard,
  updatePromptCard,
  deletePromptCard,
  createPromptGroup,
  updatePromptGroup,
  deletePromptGroup,
  initializeDefaultPromptGroups,
} from './firestoreFunctions';
import { useConfirmDialog } from '../providers/DialogProvider';

interface PromptManagerProps {
  workgroupId?: string | null;
  onCardClick: (query: string) => void;
  allowPersonalPrompts?: boolean;
}

const PromptManager: React.FC<PromptManagerProps> = ({ workgroupId = null, onCardClick, allowPersonalPrompts = true }) => {
  const [showPersonalPrompts, setShowPersonalPrompts] = useState(false);
  const [activeGroupType, setActiveGroupType] = useState<'workgroup' | 'personal'>(workgroupId ? 'workgroup' : 'personal');
  const [cards, setCards] = useState<PromptCard[]>([]);
  const [groups, setGroups] = useState<PromptGroupType[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [currentCard, setCurrentCard] = useState<PromptCard | null>(null);
  const [currentGroup, setCurrentGroup] = useState<PromptGroupType | null>(null);

  // Form state for card creation/editing
  const [cardTitle, setCardTitle] = useState('');
  const [cardDescription, setCardDescription] = useState('');
  const [cardQuery, setCardQuery] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  
  // Form state for group creation/editing
  const [groupName, setGroupName] = useState('');
  const [groupError, setGroupError] = useState('');
  
  // State for move card modal
  const [showMoveCardModal, setShowMoveCardModal] = useState(false);
  const [cardToMove, setCardToMove] = useState<PromptCard | null>(null);

  const { confirm } = useConfirmDialog();

  useEffect(() => {
    // Initialize and load data
    const loadData = async () => {
      // Determine which type of cards/groups to load
      const effectiveWorkgroupId = activeGroupType === 'workgroup' ? workgroupId : null;
      
      try {
        // Make sure at least one default group exists
        console.log("Initializing default prompt groups for:", effectiveWorkgroupId);
        await initializeDefaultPromptGroups(effectiveWorkgroupId);
        
        // Load cards and groups
        await fetchData();
      } catch (error) {
        console.error("Error initializing prompt data:", error);
      }
    };
    
    loadData();
  }, [workgroupId, activeGroupType]);

  // Toggle between workgroup and personal cards
  const toggleCardType = () => {
    const newType = activeGroupType === 'workgroup' ? 'personal' : 'workgroup';
    setActiveGroupType(newType);
  };

  const fetchData = async () => {
    try {
      // Determine which type of cards/groups to fetch
      const effectiveWorkgroupId = activeGroupType === 'workgroup' ? workgroupId : null;
      
      interface RawPromptCard {
        id: string;
        title?: string;
        description?: string;
        query?: string;
        createdAt: Date;
        createdBy?: string;
      }

      interface RawPromptGroup {
        id: string;
        name?: string;
        cards?: string[];
        isDefault?: boolean;
        workgroupId?: string;
        createdBy?: string;
        createdAt: Date;
      }

      const [fetchedCards, fetchedGroups] = await Promise.all([
        fetchPromptCards(effectiveWorkgroupId) as Promise<RawPromptCard[]>,
        fetchPromptGroups(effectiveWorkgroupId) as Promise<RawPromptGroup[]>
      ]);
      
      // Transform the fetched data to match the required types
      const typedCards: PromptCard[] = fetchedCards.map(card => ({
        id: card.id,
        title: card.title || '',
        description: card.description || '',
        query: card.query || '',
        createdAt: card.createdAt,
        createdBy: card.createdBy || ''
      }));

      const typedGroups: PromptGroupType[] = fetchedGroups.map(group => ({
        id: group.id,
        name: group.name || '',
        cards: group.cards || [],
        isDefault: group.isDefault || false,
        workgroupId: group.workgroupId,
        createdBy: group.createdBy || ''
      }));
      
      setCards(typedCards);
      setGroups(typedGroups);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleCreateCard = async () => {
    try {
      if (!cardTitle || !cardQuery) return;
      
      // Determine where to store the card
      const effectiveWorkgroupId = activeGroupType === 'workgroup' ? workgroupId : null;
      
      const newCard = await createPromptCard({
        title: cardTitle,
        description: cardDescription,
        query: cardQuery
      }, effectiveWorkgroupId);
      
      // Use selected group or default group
      const targetGroupId = selectedGroupId || groups.find(group => group.isDefault)?.id;
      
      if (targetGroupId) {
        const targetGroup = groups.find(group => group.id === targetGroupId);
        if (targetGroup) {
          // Add card to the selected group
          await updatePromptGroup(targetGroup.id, {
            ...targetGroup,
            cards: [...(targetGroup.cards || []), newCard.id]
          }, effectiveWorkgroupId);
        }
      }
      
      // Refresh data
      await fetchData();
      
      // Reset form
      resetCardForm();
      setShowCardModal(false);
    } catch (error) {
      console.error('Error creating card:', error);
    }
  };

  const handleUpdateCard = async () => {
    try {
      if (!currentCard || !cardTitle || !cardQuery) return;
      
      // Determine where the card is stored
      const effectiveWorkgroupId = activeGroupType === 'workgroup' ? workgroupId : null;
      
      await updatePromptCard(currentCard.id, {
        title: cardTitle,
        description: cardDescription,
        query: cardQuery
      }, effectiveWorkgroupId);
      
      // Refresh data
      await fetchData();
      
      // Reset form
      resetCardForm();
      setShowCardModal(false);
    } catch (error) {
      console.error('Error updating card:', error);
    }
  };

  const handleDeleteCard = async (cardId: string) => {
    console.log('🔄 Starting delete card flow for card:', cardId);
    try {
      console.log('📝 Calling custom confirm dialog...');
      const confirmed = await confirm('Are you sure you want to delete this prompt card?', 'delete_card');
      console.log('✅ Custom confirm dialog response:', confirmed);
      
      if (confirmed) {
        console.log('🗑️ User confirmed deletion, proceeding...');
        const effectiveWorkgroupId = activeGroupType === 'workgroup' ? workgroupId : null;
        await deletePromptCard(cardId, effectiveWorkgroupId);
        console.log('✨ Card deleted successfully, refreshing data...');
        await fetchData();
      } else {
        console.log('❌ User cancelled deletion');
      }
    } catch (error) {
      console.error('❌ Error in handleDeleteCard:', error);
    }
  };

  const handleCreateGroup = async () => {
    try {
      if (!groupName) return;
      
      // Clear any previous errors
      setGroupError('');
      
      // Determine where the group is stored
      const effectiveWorkgroupId = activeGroupType === 'workgroup' ? workgroupId : null;
      
      await createPromptGroup({
        name: groupName,
        cards: [],
        isDefault: false
      }, effectiveWorkgroupId);
      
      // Refresh data
      await fetchData();
      
      // Reset form
      setGroupName('');
      setShowGroupModal(false);
    } catch (error) {
      console.error('Error creating group:', error);
      // Display error message to user
      setGroupError(error.message || 'Failed to create group. Please try again.');
    }
  };

  const handleUpdateGroup = async () => {
    try {
      if (!currentGroup || !groupName) return;
      
      // Clear any previous errors
      setGroupError('');
      
      // Determine where the group is stored
      const effectiveWorkgroupId = activeGroupType === 'workgroup' ? workgroupId : null;
      
      await updatePromptGroup(currentGroup.id, {
        ...currentGroup,
        name: groupName
      }, effectiveWorkgroupId);
      
      // Refresh data
      await fetchData();
      
      // Reset form
      setGroupName('');
      setShowGroupModal(false);
    } catch (error) {
      console.error('Error updating group:', error);
      // Display error message to user
      setGroupError(error.message || 'Failed to update group. Please try again.');
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    console.log('🔄 Starting delete group flow for group:', groupId);
    try {
      console.log('📝 Calling custom confirm dialog...');
      const confirmed = await confirm('Are you sure you want to delete this group?', 'delete_group');
      console.log('✅ Custom confirm dialog response:', confirmed);
      
      if (confirmed) {
        console.log('🗑️ User confirmed deletion, proceeding...');
        await deletePromptGroup(groupId);
        console.log('✨ Group deleted successfully, refreshing data...');
        await fetchData();
      } else {
        console.log('❌ User cancelled deletion');
      }
    } catch (error) {
      console.error('❌ Error in handleDeleteGroup:', error);
    }
  };

  const openCardModal = (card: PromptCard | null = null) => {
    setCurrentCard(card);
    
    if (card) {
      // Edit mode
      setCardTitle(card.title);
      setCardDescription(card.description || '');
      setCardQuery(card.query);
      
      // Find which group(s) contain this card
      const cardGroups = groups.filter(group => 
        group.cards && group.cards.includes(card.id)
      );
      
      // If card is in a group, select the first one
      if (cardGroups.length > 0) {
        setSelectedGroupId(cardGroups[0].id);
      }
    } else {
      // Create mode
      resetCardForm();
    }
    
    setShowCardModal(true);
  };

  const openGroupModal = (group: PromptGroupType | null = null) => {
    setCurrentGroup(group);
    setGroupError(''); // Clear any previous errors
    
    if (group) {
      // Edit mode
      setGroupName(group.name);
    } else {
      // Create mode
      setGroupName('');
    }
    
    setShowGroupModal(true);
  };

  const resetCardForm = () => {
    setCardTitle('');
    setCardDescription('');
    setCardQuery('');
    setCurrentCard(null);
    
    console.log("Available groups for selection:", groups);
    
    // Set default group as selected
    const defaultGroup = groups.find(group => group.isDefault);
    if (defaultGroup) {
      console.log("Found default group:", defaultGroup.name, defaultGroup.id);
      setSelectedGroupId(defaultGroup.id);
    } else if (groups.length > 0) {
      console.log("No default group, using first group:", groups[0].name, groups[0].id);
      setSelectedGroupId(groups[0].id);
    } else {
      console.log("No groups available to select");
      setSelectedGroupId('');
    }
  };
  
  const openMoveCardModal = (card: PromptCard) => {
    setCardToMove(card);
    setShowMoveCardModal(true);
    
    // Find default group id
    const defaultGroup = groups.find(group => group.isDefault);
    if (defaultGroup) {
      setSelectedGroupId(defaultGroup.id);
    }
  };
  
  const handleMoveCard = async () => {
    try {
      if (!cardToMove || !selectedGroupId) return;
      
      // Determine where the groups are stored
      const effectiveWorkgroupId = activeGroupType === 'workgroup' ? workgroupId : null;
      
      // Find the groups that contain this card
      const groupsWithCard = groups.filter(group => 
        group.cards && group.cards.includes(cardToMove.id)
      );
      
      // Remove the card from all its current groups
      for (const group of groupsWithCard) {
        const updatedCards = (group.cards || []).filter(id => id !== cardToMove.id);
        await updatePromptGroup(group.id, {
          ...group,
          cards: updatedCards
        }, effectiveWorkgroupId);
      }
      
      // Add the card to the selected group
      const targetGroup = groups.find(g => g.id === selectedGroupId);
      if (targetGroup) {
        await updatePromptGroup(targetGroup.id, {
          ...targetGroup,
          cards: [...(targetGroup.cards || []), cardToMove.id]
        }, effectiveWorkgroupId);
      }
      
      // Refresh data
      await fetchData();
      
      // Close modal
      setShowMoveCardModal(false);
      setCardToMove(null);
      setSelectedGroupId('');
    } catch (error) {
      console.error('Error moving card:', error);
    }
  };

  // Handle toggle between personal and workgroup cards
  const handleToggleCardType = (newType: 'workgroup' | 'personal') => {
    if (newType !== activeGroupType) {
      setActiveGroupType(newType);
    }
  };

  // Update the group modal delete button handler
  const handleGroupModalDelete = async (groupId: string) => {
    console.log('🔄 Starting group modal delete flow for group:', groupId);
    try {
      console.log('📝 Calling custom confirm dialog...');
      const confirmed = await confirm('Are you sure you want to delete this group?', 'delete_group');
      console.log('✅ Custom confirm dialog response:', confirmed);
      
      if (confirmed) {
        console.log('🗑️ User confirmed deletion, proceeding...');
        await handleDeleteGroup(groupId);
        console.log('🚪 Closing group modal...');
        setShowGroupModal(false);
      } else {
        console.log('❌ User cancelled deletion');
      }
    } catch (error) {
      console.error('❌ Error in handleGroupModalDelete:', error);
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <h2 className="text-xl font-medium text-gray-700 dark:text-gray-300">Quick Prompts</h2>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setIsEditMode(!isEditMode)}
            className="px-3 py-1 text-sm rounded-md bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            {isEditMode ? 'Done' : 'Edit'}
          </button>
          
          {isEditMode && (
            <>
              <button
                onClick={() => openCardModal()}
                className="px-3 py-1 text-sm rounded-md bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-800 transition-colors"
              >
                New Prompt Card
              </button>
              <button
                onClick={() => openGroupModal()}
                className="px-3 py-1 text-sm rounded-md bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800 transition-colors"
              >
                New Group
              </button>
            </>
          )}
        </div>
      </div>
      
      {/* Toggle between personal and workgroup prompt cards */}
      {allowPersonalPrompts && workgroupId && (
        <div className="flex mb-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <button
            onClick={() => handleToggleCardType('workgroup')}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${
              activeGroupType === 'workgroup'
                ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            Workgroup Prompt Cards
          </button>
          <button
            onClick={() => handleToggleCardType('personal')}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${
              activeGroupType === 'personal'
                ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            Personal Prompt Cards
          </button>
        </div>
      )}

      {/* Render groups and cards */}
      {groups.length > 0 ? (
        groups.map((group) => (
          <PromptGroup
            key={group.id}
            group={group}
            cards={cards}
            onCardClick={onCardClick}
            onEditCard={openCardModal}
            onDeleteCard={handleDeleteCard}
            onMoveCard={openMoveCardModal}
            workgroupId={activeGroupType === 'workgroup' ? workgroupId : null}
            isEditable={isEditMode}
            onEditGroup={openGroupModal}
            onDeleteGroup={handleDeleteGroup}
          />
        ))
      ) : (
        <div className="text-center py-8 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
          <p className="text-gray-500 dark:text-gray-400 mb-2">No {activeGroupType} prompt cards available</p>
          {isEditMode && (
            <button
              onClick={() => openCardModal()}
              className="px-3 py-1 text-sm rounded-md bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-800 transition-colors"
            >
              Create your first prompt card
            </button>
          )}
        </div>
      )}

      {/* Card Modal */}
      {showCardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium mb-4 text-gray-800 dark:text-gray-200">
              {currentCard ? 'Edit Prompt Card' : 'New Prompt Card'}
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Title
              </label>
              <input
                type="text"
                value={cardTitle}
                onChange={(e) => setCardTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="What does this card ask?"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description (optional)
              </label>
              <input
                type="text"
                value={cardDescription}
                onChange={(e) => setCardDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Brief description of this query"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Query
              </label>
              <textarea
                value={cardQuery}
                onChange={(e) => setCardQuery(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="What do you want to ask?"
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Group
              </label>
              {groups.length > 0 ? (
                <select
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  style={{ borderColor: '#ddd', focusRingColor: '#279DD9' }}
                >
                  {groups.map(group => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="flex flex-col space-y-2">
                  <div className="text-yellow-600 dark:text-yellow-400 text-sm bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded-md">
                    No groups found. Creating a default "My Cards" group...
                  </div>
                  <button
                    onClick={async () => {
                      const effectiveWorkgroupId = activeGroupType === 'workgroup' ? workgroupId : null;
                      await initializeDefaultPromptGroups(effectiveWorkgroupId);
                      await fetchData();
                    }}
                    className="text-sm text-blue-600 dark:text-blue-400 underline"
                  >
                    Retry
                  </button>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowCardModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none"
              >
                Cancel
              </button>
              <button
                onClick={currentCard ? handleUpdateCard : handleCreateCard}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
              >
                {currentCard ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Group Modal */}
      {showGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium mb-4 text-gray-800 dark:text-gray-200">
              {currentGroup ? 'Edit Group' : 'New Group'}
            </h3>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Group Name
              </label>
              <input
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Enter group name"
              />
            </div>
            
            {/* Error message display */}
            {groupError && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md">
                <div className="flex items-center">
                  <svg className="h-4 w-4 text-red-500 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-red-700 dark:text-red-300">{groupError}</span>
                </div>
              </div>
            )}
            
            <div className="flex justify-between items-center">
              {currentGroup && !currentGroup.isDefault && (
                <button
                  onClick={() => handleGroupModalDelete(currentGroup.id)}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none"
                >
                  Delete Group
                </button>
              )}
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowGroupModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none"
                >
                  Cancel
                </button>
                <button
                  onClick={currentGroup ? handleUpdateGroup : handleCreateGroup}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none"
                >
                  {currentGroup ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Move Card Modal */}
      {showMoveCardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium mb-4 text-gray-800 dark:text-gray-200">
              Move Prompt Card to Different Group
            </h3>
            
            {cardToMove && (
              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Moving: <span className="font-medium text-gray-900 dark:text-gray-100">{cardToMove.title}</span>
                </p>
              </div>
            )}
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Select Group
              </label>
              <select
                value={selectedGroupId}
                onChange={(e) => setSelectedGroupId(e.target.value)}
                className="w-full px-3 py-2 text-sm border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                style={{ borderColor: '#ddd', focusRingColor: '#279DD9' }}
              >
                {groups.map(group => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowMoveCardModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none"
              >
                Cancel
              </button>
              <button
                onClick={handleMoveCard}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
              >
                Move
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptManager;
